import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, ChevronRight, Link2 } from 'lucide-react';
import { format } from 'date-fns';
import { trialRequestService } from '../../services/api';
import { TRIAL_STATUSES, statusLabel, statusClass } from './trialStatus';
import '../Tickets.css';

// Internal list of product trial requests submitted through the public /trial-request form.
export default function Trials() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', plan: '' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { page, limit: 20, ...filters, search };
    Object.keys(params).forEach(k => !params[k] && delete params[k]);
    const res = await trialRequestService.getAll(params);
    setItems(res.data.items); setTotal(res.data.total); setCounts(res.data.counts); setPages(res.data.pages);
  }, [page, filters, search]);

  useEffect(() => { load(); }, [load]);

  const formUrl = `${window.location.origin}/trial-request`;
  const copyLink = () => navigator.clipboard?.writeText(formUrl);

  return (
    <div className="tickets-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Trial Requests</h1>
          <p className="page-subtitle">
            {total} request{total === 1 ? '' : 's'}{counts.new ? ` · ${counts.new} new` : ''}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={copyLink} title={formUrl}>
          <Link2 size={14} /> Copy public form link
        </button>
      </div>

      <div className="tickets-toolbar">
        <div className="search-box">
          <Search size={14} />
          <input placeholder="Search company, contact, email, reference..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <div className="filters">
          <Filter size={14} color="var(--text-muted)" />
          <select value={filters.status} onChange={e => { setFilters(f => ({ ...f, status: e.target.value })); setPage(1); }}>
            <option value="">All Statuses</option>
            {TRIAL_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}{counts[v] ? ` (${counts[v]})` : ''}</option>)}
          </select>
          <select value={filters.plan} onChange={e => { setFilters(f => ({ ...f, plan: e.target.value })); setPage(1); }}>
            <option value="">All Plans</option>
            {['Silver', 'Gold', 'Platinum'].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>

      <div className="ticket-table card">
        <table>
          <thead>
            <tr><th>Reference</th><th>Company</th><th>Contact</th><th>Plan</th><th>Users</th><th>Status</th><th>Submitted</th><th></th></tr>
          </thead>
          <tbody>
            {items.map(t => (
              <tr key={t._id} className="ticket-row" onClick={() => navigate(`/trials/${t._id}`)}>
                <td><span className="ticket-num">{t.reference}</span></td>
                <td><div className="ticket-title">{t.company}</div><div className="ticket-category">{t.product}</div></td>
                <td><div>{t.contactName}</div><div className="ticket-category">{t.email}</div></td>
                <td>{t.plan}</td>
                <td>{t.users}</td>
                <td><span className={`badge ${statusClass(t.status)}`}>{statusLabel(t.status)}</span></td>
                <td><div className="ticket-time">{t.emailVerifiedAt && format(new Date(t.emailVerifiedAt), 'dd MMM yyyy, HH:mm')}</div></td>
                <td><ChevronRight size={16} color="var(--text-muted)" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="empty-state">No trial requests found</div>}
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, alignItems: 'center' }}>
          <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
          <span className="text-muted">Page {page} of {pages}</span>
          <button className="btn btn-ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
